import { fi, sv, enUS } from "date-fns/locale";
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// Import JSON files as modules
import { SUPPORTED_LANGUAGES } from "./i18nConstants";
import enTranslations from "./locales/en.json";
import fiTranslations from "./locales/fi.json";
import svTranslations from "./locales/sv.json";
import { replaceLanguageInPath } from "../../common/utils/pathUtils";
import { DEFAULT_LANG } from "../app/appConstants";
import history from "../app/appHistory";

const getTranslations = () => ({
  fi: {
    translation: fiTranslations,
  },
  sv: {
    translation: svTranslations,
  },
  en: {
    translation: enTranslations,
  },
});

const localesByName = getTranslations();
const supportedLanguages = Object.values(SUPPORTED_LANGUAGES);

function changeDocumentLanguage(nextLanguage: string) {
  document.documentElement.lang = nextLanguage;
}

// Auto-detected language generally has a country code. Ignore it.
export const getCurrentLanguage = () => i18n.language.split("-")[0];

// Get date-fns locale based on current language
export const getDateFnsLocale = () => {
  const language = getCurrentLanguage();
  switch (language) {
    case 'fi': return fi;
    case 'sv': return sv;
    case 'en': return enUS;
    default: return fi; // Default to Finnish
  }
};

// Configure language changed hook first so that it is fired on language
// initialization as well.
i18n.on("languageChanged", (nextLanguage) => {
  changeDocumentLanguage(nextLanguage);
});

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(
    {
      load: "languageOnly",
      supportedLngs: supportedLanguages,
      resources: localesByName,
      fallbackLng: DEFAULT_LANG,
      interpolation: {
        escapeValue: false,
      },
      detection: {
        order: ["path", "localStorage", "navigator"],
      },
    },
    (err, t) => {
      if (err) {
        // eslint-disable-next-line no-console
        console.log(err, t);
      }
    },
  );

// replace language in url so that the pathname will reflect the current
// language when language is changed by using i18n.changeLanguage
// Replace language only when it is changed, not on initialization.
i18n.on("languageChanged", (nextLanguage) => {
  // If necessary, change language in pathname
  const { pathname, ...rest } = globalThis.location;

  const containsLanguage = supportedLanguages.reduce(
    (contains, language) => contains || pathname.includes(`/${language}/`),
    false,
  );

  if (containsLanguage) {
    const nextPathname = replaceLanguageInPath(pathname, nextLanguage);

    history.replace({
      pathname: nextPathname,
      ...rest,
    });
  }
});

export default i18n; // NOSONAR - This is not a re-export, it's exporting the configured i18n instance
